import { useQuery } from "@tanstack/react-query";
import { History, LogOut, Plus } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { SlotApproversCard } from "../releases/SlotApproversCard";
import { api } from "../../shared/api/client";
import {
  Button,
  Card,
  DataTable,
  ErrorState,
  Field,
  InlineAlert,
  Input,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import type { ProjectMemberView } from "../../shared/types/api";
import { AddProjectMemberDrawer, CloseProjectMemberDrawer } from "./ProjectMemberDrawers";
import { PROJECT_ROLE_LABELS } from "./projectVocabulary";

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Who is on the project, on a given date (§13).
 *
 * <p>The same effective-dated model as team membership, and for the same reason: "who was on this
 * project in March" has to stay answerable in December. Involvement is closed on a date, never
 * deleted — a worklog from March would otherwise point at a person the project claims was never
 * there.
 */
export function ProjectMembersTab({ projectId }: { projectId: string }) {
  const { can } = useAuth();
  const [asOf, setAsOf] = useState(today());
  const [adding, setAdding] = useState(false);
  const [closing, setClosing] = useState<ProjectMemberView | null>(null);

  const isHistorical = asOf !== today();
  const manage = can("PROJECT_MEMBER_MANAGE");

  const members = useQuery({
    queryKey: ["project-members", projectId, asOf],
    queryFn: () =>
      api.get<ProjectMemberView[]>(`/api/v1/projects/${projectId}/members?asOf=${asOf}`),
  });

  const columns: Column<ProjectMemberView>[] = [
    {
      key: "fullName",
      header: "Employee",
      render: (member) => <span className="font-medium text-ink-900">{member.fullName}</span>,
    },
    {
      key: "employeeCode",
      header: "Code",
      render: (member) => (
        <span className="font-mono text-xs text-ink-500">{member.employeeCode}</span>
      ),
    },
    {
      key: "projectRole",
      header: "Role",
      render: (member) => (
        <StatusBadge tone="blue">{PROJECT_ROLE_LABELS[member.projectRole]}</StatusBadge>
      ),
    },
    { key: "effectiveFrom", header: "From", className: "tnum" },
    {
      key: "effectiveTo",
      header: "To",
      className: "tnum",
      render: (member) => member.effectiveTo ?? <span className="text-ink-400">open-ended</span>,
    },
  ];

  if (manage && !isHistorical) {
    columns.push({
      key: "actions",
      header: "",
      className: "text-right",
      render: (member) => (
        <Button
          variant="ghost"
          size="sm"
          title="End this involvement"
          className="text-amber-600 hover:bg-amber-50 hover:text-amber-700"
          onClick={() => setClosing(member)}
        >
          <LogOut className="h-4 w-4" />
        </Button>
      ),
    });
  }

  return (
    <>
      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-4 p-4">
          <div className="w-52">
            <Field label="Members as of">
              <Input
                type="date"
                value={asOf}
                onChange={(event) => setAsOf(event.target.value)}
              />
            </Field>
          </div>

          {isHistorical && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <History className="h-4 w-4 shrink-0" />
              Historical view
            </div>
          )}

          <div className="ml-auto flex gap-2">
            {isHistorical && (
              <Button variant="ghost" size="sm" onClick={() => setAsOf(today())}>
                Back to today
              </Button>
            )}
            {manage && !isHistorical && (
              <Button icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
                Add member
              </Button>
            )}
          </div>
        </div>
      </Card>

      {isHistorical && (
        <div className="mb-4">
          <InlineAlert tone="info">
            This is who was on the project on {asOf}. Switch back to today to change involvement.
          </InlineAlert>
        </div>
      )}

      {members.isError ? (
        <ErrorState error={members.error} onRetry={() => void members.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={members.data ?? []}
          rowKey={(member) => member.membershipId}
          loading={members.isPending}
          emptyTitle={isHistorical ? `Nobody was on this project on ${asOf}` : "No members yet"}
          emptyMessage={
            isHistorical
              ? "Involvement is recorded with dates, so this is the answer for that day — not a gap in the data."
              : "Add people from the date their involvement starts. Their project role decides what they can do here, on top of their organisation role."
          }
          emptyAction={
            manage &&
            !isHistorical && (
              <Button icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
                Add the first member
              </Button>
            )
          }
        />
      )}

      <div className="mt-4">
        <SlotApproversCard projectId={projectId} />
      </div>

      <AddProjectMemberDrawer
        projectId={projectId}
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={() => void members.refetch()}
      />
      <CloseProjectMemberDrawer
        projectId={projectId}
        member={closing}
        onClose={() => setClosing(null)}
        onSaved={() => void members.refetch()}
      />
    </>
  );
}
